/**
 * Submit Policy Agent for Ameen Insurance Assistant
 *
 * Handles post-vehicle-inspection policy submission and payment (Step 6).
 * Activated AFTER Vehicle Inspection when caller was policy_agent.
 */

import { AgentNode, AgentContext, ToolCallResult, VoiceGender, UIStateManager } from '../types';
import { noEntryRequirements } from '../guardrails';
import { logger } from '../../utils/logger';
import { submitPolicyRequest, confirmPolicyRequest } from '../../services/policyService';
import { handleSearchFAQ } from './RouterAgent';

/**
 * Create Submit Policy Agent node
 */
export function createSubmitPolicyAgent(voiceGender: VoiceGender): AgentNode {
  return {
    id: 'submit_policy_agent',
    name: 'Submit Policy Agent',
    description: 'Handles post-inspection policy submission and payment',

    entryGuardrails: noEntryRequirements(),

    exitGuardrails: {
      transitions: new Map([
        ['go_to_policy', { target: { agentId: 'policy_agent' }, guardrails: [] }],
        ['go_to_router', { target: { agentId: 'router_agent' }, guardrails: [] }],
        ['go_to_claim', { target: { agentId: 'claim_agent' }, guardrails: [] }],
      ]),
      fallback: { agentId: 'router_agent' },
    },

    initialPrompt: 'متابعة بعد المعاينة.',

    onActivate: async (context: AgentContext) => {
      logger.info('[SubmitPolicyAgent] Activated');
      if (context.transitionData.damage_description) {
        logger.info('[SubmitPolicyAgent] Received damage description from inspection');
      }
    },

    handleToolCall: async (
      toolName: string,
      args: Record<string, any>,
      context: AgentContext,
      uiState?: UIStateManager
    ): Promise<ToolCallResult> => {
      logger.tool('[SubmitPolicyAgent] Tool called:', toolName, args);

      switch (toolName) {
        case 'go_to_router_agent':
          return {
            toolName,
            args,
            result: { success: true, message: 'Returning to menu' },
            triggersTransition: true,
            transitionTo: 'router_agent',
            transitionDelayMs: -1,
            transitionData: { return_to_menu: true },
          };

        case 'go_to_policy_agent':
          return {
            toolName,
            args,
            result: { success: true, message: 'Switching to policy flow' },
            triggersTransition: true,
            transitionTo: 'policy_agent',
            transitionDelayMs: -1,
          };

        case 'go_to_claim_agent':
          return {
            toolName,
            args,
            result: { success: true, message: 'Switching to claim flow' },
            triggersTransition: true,
            transitionTo: 'claim_agent',
            transitionDelayMs: -1,
          };

        case 'submit_policy_request':
          return await handleSubmitPolicyRequest(toolName, uiState);

        case 'confirm_policy_request':
          return await handleConfirmPolicyRequest(args, toolName, uiState);

        case 'search_faq':
          return await handleSearchFAQ(args, toolName);

        default:
          logger.warn('[SubmitPolicyAgent] Unknown tool:', toolName);
          return {
            toolName,
            args,
            result: { success: false, error: `Unknown tool: ${toolName}` },
          };
      }
    },

    onDeactivate: async (context: AgentContext) => {
      logger.info('[SubmitPolicyAgent] Deactivated');
    },
  };
}

/**
 * Handle submit_policy_request tool
 */
async function handleSubmitPolicyRequest(
  toolName: string,
  uiState?: UIStateManager
): Promise<ToolCallResult> {
  try {
    logger.info('[SubmitPolicyAgent] Submitting policy request');
    uiState?.showLoading?.('جاري تسجيل طلب الوثيقة...', 'api');

    const result = await submitPolicyRequest();
    logger.info('[SubmitPolicyAgent] Policy request submitted:', result);
    uiState?.hideLoading?.();

    if (uiState && result.requestId) {
      uiState.showPolicyNumber(
        result.requestId,
        'رقم طلب الوثيقة',
        'تم تسجيل طلب الوثيقة بنجاح. يرجى حفظ رقم الطلب:',
        () => {
          logger.info('[SubmitPolicyAgent] User confirmed policy request number (clicked Save on request number popup)');
          // Show confirm package screen only when user clicks Save on request number popup (client is established by then)
          uiState.setFlowStep('policy_confirmation');
        }
      );
    }

    return {
      toolName,
      args: {},
      result: {
        success: true,
        requestId: result.requestId,
        urlDraft: result.urlDraft,
        message: `Policy request submitted. Request ID: ${result.requestId}.`,
      },
    };
  } catch (error: any) {
    uiState?.hideLoading?.();
    logger.error('[SubmitPolicyAgent] Error submitting policy request:', error);
    return {
      toolName,
      args: {},
      result: {
        success: false,
        error: error.message || 'Failed to submit policy request',
      },
    };
  }
}

/**
 * Handle confirm_policy_request tool
 */
async function handleConfirmPolicyRequest(
  args: Record<string, any>,
  toolName: string,
  uiState?: UIStateManager
): Promise<ToolCallResult> {
  const requestId = args.requestId as string;

  if (!requestId) {
    return {
      toolName,
      args,
      result: {
        success: false,
        error: 'Missing required requestId parameter for confirm_policy_request',
      },
    };
  }

  try {
    logger.info('[SubmitPolicyAgent] Confirming policy request:', requestId);
    uiState?.showLoading?.('جاري إنشاء رابط الدفع...', 'api');

    const message = await confirmPolicyRequest(requestId);
    logger.info('[SubmitPolicyAgent] Policy confirmed, result:', message);
    uiState?.hideLoading?.();

    const urlRegex = /(https?:\/\/[^\s]+)/i;
    const linkMatch = message.match(urlRegex);
    const payLink = linkMatch ? linkMatch[0] : message;

    if (uiState && payLink) {
      uiState.showPolicyNumber(
        payLink,
        'رابط الدفع',
        'من فضلك ادفع من خلال الرابط التالي لاستكمال طلبك:',
        () => {
          logger.info('[SubmitPolicyAgent] User acknowledged payment link');
        }
      );
    }

    return {
      toolName,
      args,
      result: {
        success: true,
        requestId,
        message,
      },
    };
  } catch (error: any) {
    uiState?.hideLoading?.();
    logger.error('[SubmitPolicyAgent] Error confirming policy request:', error);
    return {
      toolName,
      args,
      result: {
        success: false,
        error: error.message || 'Failed to confirm policy request. Please try again later.',
      },
    };
  }
}
